import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { debounce } from 'lodash-es'
import { Modal } from 'ant-design-vue'
import { usePagination } from '@/composables/usePagination'
import { usePrintLoading } from '@/composables/usePrintLoading'
import { useUserStore } from '@/stores/user'
import { appendProductImageToken } from '@/utils/productImageToken'

export function useProductList() {
  const router = useRouter()
  const userStore = useUserStore()

  // 商品列表數據
  const products = ref([])
  const loading = ref(false)
  const selectedProductIds = ref([])

  // 搜尋條件
  const searchText = ref('')
  const skuSearchText = ref('')
  const selectedCategoryId = ref(null)
  const selectedStatus = ref('enabled')

  // 最近點進編輯頁的商品，返回列表時用來定位
  const lastViewedProductId = ref(null)

  // 排序狀態
  const sortField = ref('created_at')
  const sortOrder = ref('descend')

  // 分類數據
  const categories = ref([])
  const categoryTreeData = ref([])

  // 分頁管理
  const pagination = usePagination()
  const {
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    paginationConfig,
    handlePageChange: paginationHandlePageChange,
    handlePageSizeChange: paginationHandlePageSizeChange,
    scrollToTop
  } = pagination

  // 頁碼持久化，離開列表（例如進入編輯頁）再回來時停留在原本那一頁
  watch(currentPage, (page) => {
    if (page > 1) {
      localStorage.setItem('productList_page', String(page))
    } else {
      localStorage.removeItem('productList_page')
    }
  })

  // 列印載入管理
  const {
    LOADING_STEPS,
    executePrintWithLoading,
    updateProgress
  } = usePrintLoading()

  // 構建分類樹
  function buildCategoryTree(flatData) {
    const map = {}
    flatData.forEach(item => {
      map[item.id] = { ...item, id: item.id, name: item.name, children: [] }
    })

    const tree = []
    flatData.forEach(item => {
      const node = map[item.id]
      if (item.parent_id) {
        if (map[item.parent_id]) {
          map[item.parent_id].children.push(node)
          map[item.parent_id].children.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        } else {
          tree.push(node)
        }
      } else {
        tree.push(node)
      }
    })

    tree.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    return [{ id: null, name: '全部類型' }, ...tree]
  }

  // 獲取分類的完整路徑
  function getCategoryFullPath(product) {
    if (product.category_name && product.category_name.includes(' > ')) {
      return product.category_name
    }

    if (product.category_id && categories.value.length) {
      const categoryMap = {}
      categories.value.forEach(category => {
        categoryMap[category.id] = category
      })

      const buildPath = (id) => {
        const result = []
        let currentId = id
        let safetyCounter = 0
        while (currentId && safetyCounter < 20) {
          const category = categoryMap[currentId]
          if (!category) break
          result.unshift(category.name)
          currentId = category.parent_id
          safetyCounter++
        }
        return result.join(' > ')
      }

      return buildPath(product.category_id)
    }

    if (product.category_name) {
      return product.category_name
    }

    return '未分類'
  }

  // 獲取分類的簡化路徑
  function getCategoryShortPath(product) {
    const fullPath = getCategoryFullPath(product)
    if (!fullPath || fullPath === '未分類') {
      return fullPath
    }

    const pathParts = fullPath.split(' > ')
    if (pathParts.length <= 2) {
      return fullPath
    } else {
      const lastTwoParts = pathParts.slice(-2)
      return lastTwoParts.join(' > ')
    }
  }

  // 獲取分類列表
  async function fetchCategories() {
    try {
      const response = await axios.get('/admin/categories')
      let flatData = []
      if (response.data.flat && Array.isArray(response.data.flat)) {
        flatData = response.data.flat
      } else if (Array.isArray(response.data)) {
        flatData = response.data
      } else {
        ElMessage.error('無法載入分類列表')
        return
      }
      categories.value = flatData
      categoryTreeData.value = buildCategoryTree(flatData)
    } catch (error) {
      ElMessage.error('獲取分類列表失敗')
    }
  }

  // 獲取所選分類及其所有子分類的ID列表
  function getAllChildCategoryIds(categoryId) {
    if (!categoryId) return []

    const result = [categoryId]

    function getChildIds(parentId) {
      const children = categories.value.filter(cat => cat.parent_id === parentId)
      children.forEach(child => {
        result.push(child.id)
        getChildIds(child.id)
      })
    }

    getChildIds(categoryId)
    return result
  }

  // 搜尋輸入處理
  const onSearchInput = debounce(() => {
    localStorage.setItem('productList_searchText', searchText.value)
    currentPage.value = 1
    fetchProducts()
  }, 500)

  // 清除搜尋文字
  function clearSearchText() {
    searchText.value = ''
    localStorage.removeItem('productList_searchText')
    currentPage.value = 1
    fetchProducts()
  }

  // SKU 搜尋輸入處理
  const onSkuSearchInput = debounce(() => {
    localStorage.setItem('productList_skuSearchText', skuSearchText.value)
    currentPage.value = 1
    fetchProducts()
  }, 500)

  // 清除 SKU 搜尋文字
  function clearSkuSearchText() {
    skuSearchText.value = ''
    localStorage.removeItem('productList_skuSearchText')
    currentPage.value = 1
    fetchProducts()
  }

  // 分類變更處理
  function onCategoryChange() {
    if (selectedCategoryId.value) {
      localStorage.setItem('productList_categoryId', JSON.stringify(selectedCategoryId.value))
    } else {
      localStorage.removeItem('productList_categoryId')
    }
    currentPage.value = 1
    fetchProducts()
  }

  // 狀態變更處理
  function onStatusChange() {
    localStorage.setItem('productList_status', selectedStatus.value)
    currentPage.value = 1
    fetchProducts()
  }

  // 處理分類下拉框的顯示/隱藏事件
  function handleCategoryVisibleChange(visible) {
    if (visible && categories.value.length === 0) {
      fetchCategories()
    }
  }

  // 重置過濾條件
  function resetFilters() {
    selectedCategoryId.value = null
    selectedStatus.value = 'enabled'

    localStorage.removeItem('productList_categoryId')
    localStorage.removeItem('productList_status')

    currentPage.value = 1
    fetchProducts()
  }

  // 獲取商品列表數據
  async function fetchProducts() {
    loading.value = true
    selectedProductIds.value = []

    const params = {
      page: currentPage.value,
      limit: pageSize.value
    }

    if (searchText.value && searchText.value.trim()) {
      params.search = searchText.value.trim()
    }

    if (skuSearchText.value && skuSearchText.value.trim()) {
      params.sku = skuSearchText.value.trim()
    }

    if (sortField.value && sortOrder.value) {
      params.sortField = sortField.value
      params.sortOrder = sortOrder.value === 'ascend' ? 'asc' : 'desc'
    }

    if (selectedCategoryId.value) {
      const categoryIds = getAllChildCategoryIds(selectedCategoryId.value)
      params.categoryIds = categoryIds.join(',')
    }

    if (selectedStatus.value !== 'all') {
      params.isActive = selectedStatus.value === 'enabled'
    }

    try {
      const response = await axios.get('/admin/products', { params })
      if (response.data && Array.isArray(response.data.products)) {
        products.value = response.data.products.map(p => ({
          ...p,
          is_active: Boolean(p.is_active),
          updatingStatus: false,
          editingPrice: false,
          editingStock: false,
          savingPrice: false,
          savingStock: false,
          copying: false,
          tempPrice: p.retail_price,
          tempStock: p.stock_quantity
        }))

        if (response.data.pagination) {
          totalItems.value = response.data.pagination.total || 0

          const newTotalPages = Math.ceil(totalItems.value / pageSize.value)
          if (currentPage.value > newTotalPages && newTotalPages > 0) {
            currentPage.value = newTotalPages
            loading.value = false
            fetchProducts()
            return
          }

          currentPage.value = response.data.pagination.current_page || 1
        } else {
          totalItems.value = products.value.length
        }
      } else {
        products.value = []
        totalItems.value = 0
        ElMessage.error('無法載入商品列表，數據格式錯誤')
      }
    } catch (error) {
      products.value = []
      totalItems.value = 0
      ElMessage.error(error.response?.data?.error || error.message || '獲取商品列表失敗')
    } finally {
      loading.value = false
    }
  }

  // 處理排序變更
  function handleSortChange(sorter) {
    if (sorter && sorter.column) {
      sortField.value = sorter.column
      sortOrder.value = sorter.order || 'descend'
    } else {
      sortField.value = 'created_at'
      sortOrder.value = 'descend'
    }
    currentPage.value = 1
    fetchProducts()
  }

  // 處理分頁變更
  function handlePageChange(page, size) {
    paginationHandlePageChange(page, size)
    fetchProducts()
    scrollToTop()
  }

  function handlePageSizeChange(current, size) {
    paginationHandlePageSizeChange(current, size)
    fetchProducts()
    scrollToTop()
  }

  // 處理啟用/停用
  async function handleStatusChange(product, newStatus) {
    const productIndex = products.value.findIndex(p => p.id === product.id)
    if (productIndex === -1) return

    products.value[productIndex] = { ...products.value[productIndex], updatingStatus: true }

    try {
      const payload = { is_active: newStatus }
      if (!newStatus) {
        payload.is_listed = false
      }

      await axios.put(`/admin/products/${product.id}`, payload)

      products.value[productIndex].is_active = newStatus
      if (!newStatus) {
        products.value[productIndex].is_listed = false
      }
      ElMessage.success(
        newStatus
          ? `商品 "${product.name}" 已啟用`
          : `商品 "${product.name}" 已停用，並自動下架`
      )
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || '更新狀態失敗'
      ElMessage.error(errorMsg)
      products.value[productIndex].is_active = !newStatus
    } finally {
      if (products.value[productIndex]) {
        products.value[productIndex] = { ...products.value[productIndex], updatingStatus: false }
      }
    }
  }

  // 處理上架/下架（前台是否可下單）
  async function handleListedChange(product, newListed) {
    const productIndex = products.value.findIndex(p => p.id === product.id)
    if (productIndex === -1) return

    if (newListed && !product.is_active) {
      products.value[productIndex].is_listed = false
      ElMessage.warning('商品已停用，請先啟用後才能上架')
      return
    }

    products.value[productIndex] = { ...products.value[productIndex], updatingListed: true }

    try {
      await axios.put(`/admin/products/${product.id}`, {
        is_listed: newListed
      })

      products.value[productIndex].is_listed = newListed
      ElMessage.success(`商品 "${product.name}" 已${newListed ? '上架' : '下架'}`)
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || '更新上架狀態失敗'
      ElMessage.error(errorMsg)
      products.value[productIndex].is_listed = !newListed
    } finally {
      if (products.value[productIndex]) {
        products.value[productIndex] = { ...products.value[productIndex], updatingListed: false }
      }
    }
  }

  // 處理刪除所選商品
  async function handleDeleteSelected() {
    if (selectedProductIds.value.length === 0) {
      ElMessage.warning('請先選擇要刪除的商品')
      return
    }

    try {
      await ElMessageBox.confirm(
        `確定要刪除選中的 ${selectedProductIds.value.length} 個商品嗎？此操作無法復原。`,
        '確認刪除',
        {
          confirmButtonText: '確定刪除',
          cancelButtonText: '取消',
          type: 'warning'
        }
      )

      loading.value = true
      const deletePromises = selectedProductIds.value.map(id =>
        axios.delete(`/admin/products/${id}`)
      )

      const results = await Promise.allSettled(deletePromises)

      let successCount = 0
      let failCount = 0
      results.forEach((result, index) => {
        const productId = selectedProductIds.value[index]
        if (result.status === 'fulfilled') {
          successCount++
        } else {
          failCount++
          const errorMessage = result.reason?.response?.data?.error || result.reason?.message || '未知錯誤'
          let userFriendlyError = errorMessage

          if (errorMessage.includes('刪除產品失敗') ||
              errorMessage.toLowerCase().includes('foreign key constraint failed') ||
              errorMessage.toLowerCase().includes('constraint')) {
            userFriendlyError = '此商品已有關聯訂單或庫存記錄，無法刪除。您可以停用此商品而非刪除。'
          }

          ElMessage.error(`刪除商品 ID ${productId} 失敗: ${userFriendlyError}`)
        }
      })

      if (successCount > 0) {
        ElMessage.success(`成功刪除 ${successCount} 個商品`)
      }
      if (failCount === 0) {
        selectedProductIds.value = []
      }

      await fetchProducts()
    } catch (error) {
      if (error !== 'cancel') {
        ElMessage.error('刪除過程中發生錯誤')
      } else {
        ElMessage.info('已取消刪除操作')
      }
    } finally {
      loading.value = false
    }
  }

  // 處理複製商品
  function handleCopyProduct(record) {
    Modal.confirm({
      title: '確認複製商品',
      content: `確定要複製商品「${record.name}」嗎？`,
      okText: '確定複製',
      cancelText: '取消',
      type: 'info',
      centered: true,
      maskClosable: false,
      keyboard: false,
      onOk() {
        return new Promise(async (resolve, reject) => {
          try {
            record.copying = true
            const response = await axios.post(`/admin/products/${record.id}/copy`)

            if (response.data && response.data.newProductId) {
              ElMessage.success('商品複製成功！即將跳轉到編輯頁面')
              setTimeout(() => {
                router.push({
                  path: `/products/edit/${response.data.newProductId}`,
                  query: { fromCopy: 'true' }
                })
              }, 1000)
              resolve()
            } else {
              ElMessage.error('複製成功但無法獲取新商品ID')
              reject(new Error('無法獲取新商品ID'))
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '複製商品失敗'
            ElMessage.error(errorMsg)
            reject(error)
          } finally {
            record.copying = false
          }
        })
      },
      onCancel() {
        ElMessage.info('已取消複製操作')
      }
    })
  }

  // 導航到商品創建頁面
  function navigateToCreateProduct() {
    if (selectedCategoryId.value) {
      router.push({
        path: '/products/create',
        query: { categoryId: selectedCategoryId.value }
      })
    } else {
      router.push('/products/create')
    }
  }

  // 導航到商品編輯頁面
  function navigateToEdit(productId) {
    lastViewedProductId.value = productId
    localStorage.setItem('productList_lastViewedId', String(productId))
    router.push(`/products/edit/${productId}`)
  }

  // 返回列表時捲動到剛才點進去的那筆商品，並短暫標示
  function scrollToLastViewedProduct() {
    const productId = lastViewedProductId.value
    if (!productId) return

    lastViewedProductId.value = null
    localStorage.removeItem('productList_lastViewedId')

    nextTick(() => {
      requestAnimationFrame(() => {
        const target = document.querySelector(
          `.product-list-mobile [data-product-id="${productId}"], .product-list-page [data-row-key="${productId}"]`
        )
        if (!target) return

        target.scrollIntoView({ block: 'center' })
        target.classList.add('is-last-viewed')
        setTimeout(() => target.classList.remove('is-last-viewed'), 2000)
      })
    })
  }

  // 導航到前台商品頁面
  async function goToFrontendProduct(productId) {
    const currentDatabase = userStore.user?.database || 'chengshe'
    let frontendUrl
    if (currentDatabase === 'hangsian') {
      frontendUrl = `https://hangsian.tw/products/${productId}`
    } else {
      frontendUrl = `https://www.chengshe.tw/products/${productId}`
    }

    try {
      await navigator.clipboard.writeText(frontendUrl)
      ElMessage.success('已複製前台網址')
    } catch (error) {
      try {
        const textArea = document.createElement('textarea')
        textArea.value = frontendUrl
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        ElMessage.success('已複製前台網址')
      } catch (fallbackError) {
        ElMessage.error('複製網址失敗，請手動複製')
      }
    }

    window.open(frontendUrl, '_blank')
  }

  // 複製商品名稱
  async function copyProductName(productName) {
    if (!productName || productName === '未設定') {
      ElMessage.warning('此商品尚未設定名稱')
      return
    }

    try {
      const cleanName = productName.trim()
      await navigator.clipboard.writeText(cleanName)
      ElMessage.success(`已複製商品名稱: ${cleanName}`)
    } catch (error) {
      try {
        const textArea = document.createElement('textarea')
        textArea.value = productName.trim()
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        ElMessage.success(`已複製商品名稱: ${productName.trim()}`)
      } catch (fallbackError) {
        ElMessage.error('複製失敗，請手動選取複製')
      }
    }
  }

  // 複製SKU
  async function copySku(sku) {
    if (!sku || sku === '未設定') {
      ElMessage.warning('此商品尚未設定SKU')
      return
    }

    try {
      const cleanSku = sku.trim()
      await navigator.clipboard.writeText(cleanSku)
      ElMessage.success(`已複製SKU: ${cleanSku}`)
    } catch (error) {
      try {
        const textArea = document.createElement('textarea')
        textArea.value = sku.trim()
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        ElMessage.success(`已複製SKU: ${sku.trim()}`)
      } catch (fallbackError) {
        ElMessage.error('複製失敗，請手動選取複製')
      }
    }
  }

  // 檢測 URL 是否為 IP 地址
  function isIpAddress(url) {
    if (!url) return false
    try {
      const urlObj = new URL(url)
      const hostname = urlObj.hostname
      const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/
      const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/
      return ipv4Regex.test(hostname) || ipv6Regex.test(hostname)
    } catch {
      return false
    }
  }

  // 獲取圖片 URL
  function buildImageUrl(imagePath) {
    if (!imagePath) return ''

    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      if (typeof window !== 'undefined' && window.location.protocol === 'https:' && imagePath.startsWith('http://')) {
        if (isIpAddress(imagePath)) {
          try {
            const urlObj = new URL(imagePath)
            const relativePath = urlObj.pathname + (urlObj.search || '')
            const currentDatabase = userStore.user?.database
            if (currentDatabase && (currentDatabase === 'chengshe' || currentDatabase === 'hangsian')) {
              const separator = relativePath.includes('?') ? '&' : '?'
              return `${relativePath}${separator}database=${currentDatabase}`
            }
            return relativePath
          } catch {
            return imagePath
          }
        } else {
          return imagePath.replace('http://', 'https://')
        }
      }
      return imagePath
    }

    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && import.meta.env.PROD) {
      const formattedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`
      const currentDatabase = userStore.user?.database

      if (currentDatabase && (currentDatabase === 'chengshe' || currentDatabase === 'hangsian')) {
        const separator = formattedPath.includes('?') ? '&' : '?'
        return `${formattedPath}${separator}database=${currentDatabase}`
      }

      return formattedPath
    }

    const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080'
    const formattedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`
    const currentDatabase = userStore.user?.database

    // 在 HTTPS 頁面上，如果後端 URL 是 IP 地址的 HTTP URL，使用相對路徑避免混合內容問題
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      if (backendUrl.startsWith('http://') && isIpAddress(backendUrl)) {
        // 使用相對路徑，並添加資料庫參數
        if (currentDatabase && (currentDatabase === 'chengshe' || currentDatabase === 'hangsian')) {
          const separator = formattedPath.includes('?') ? '&' : '?'
          return `${formattedPath}${separator}database=${currentDatabase}`
        }
        return formattedPath
      }
      if (backendUrl.startsWith('http://') && !isIpAddress(backendUrl)) {
        const httpsBackendUrl = backendUrl.replace('http://', 'https://')
        if (currentDatabase && (currentDatabase === 'chengshe' || currentDatabase === 'hangsian')) {
          const separator = formattedPath.includes('?') ? '&' : '?'
          return `${httpsBackendUrl}${formattedPath}${separator}database=${currentDatabase}`
        }
        return `${httpsBackendUrl}${formattedPath}`
      }
      if (backendUrl.startsWith('https://')) {
        if (currentDatabase && (currentDatabase === 'chengshe' || currentDatabase === 'hangsian')) {
          const separator = formattedPath.includes('?') ? '&' : '?'
          return `${backendUrl}${formattedPath}${separator}database=${currentDatabase}`
        }
        return `${backendUrl}${formattedPath}`
      }
      // 其他情況使用相對路徑
      if (currentDatabase && (currentDatabase === 'chengshe' || currentDatabase === 'hangsian')) {
        const separator = formattedPath.includes('?') ? '&' : '?'
        return `${formattedPath}${separator}database=${currentDatabase}`
      }
      return formattedPath
    }

    // 非 HTTPS 頁面，直接使用後端 URL
    if (currentDatabase && (currentDatabase === 'chengshe' || currentDatabase === 'hangsian')) {
      const separator = formattedPath.includes('?') ? '&' : '?'
      return `${backendUrl}${formattedPath}${separator}database=${currentDatabase}`
    }
    return `${backendUrl}${formattedPath}`
  }

  // 商品圖片另外附上帳號通行證，後端才會放行
  function getImageUrl(imagePath) {
    return appendProductImageToken(buildImageUrl(imagePath))
  }

  // 格式化貨幣
  function formatCurrency(value) {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '-'
    return `$${Number(value).toLocaleString()}`
  }

  // 格式化日期時間
  function formatDateTime(dateTimeString) {
    if (!dateTimeString) return '-'
    try {
      const date = new Date(dateTimeString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch (e) {
      return dateTimeString
    }
  }

  // 初始化：從 localStorage 恢復搜尋條件
  function initializeFilters() {
    const savedCategoryId = localStorage.getItem('productList_categoryId')
    if (savedCategoryId) {
      try {
        const parsedCategoryId = JSON.parse(savedCategoryId)
        selectedCategoryId.value = parsedCategoryId
      } catch (error) {
        // 忽略解析錯誤
      }
    }

    const savedStatus = localStorage.getItem('productList_status')
    if (savedStatus) {
      selectedStatus.value = savedStatus
    }

    const savedSearchText = localStorage.getItem('productList_searchText')
    if (savedSearchText) {
      searchText.value = savedSearchText
    }

    const savedSkuSearchText = localStorage.getItem('productList_skuSearchText')
    if (savedSkuSearchText) {
      skuSearchText.value = savedSkuSearchText
    }

    const savedPage = parseInt(localStorage.getItem('productList_page'), 10)
    if (!isNaN(savedPage) && savedPage > 1) {
      currentPage.value = savedPage
    }

    const savedLastViewedId = localStorage.getItem('productList_lastViewedId')
    if (savedLastViewedId) {
      lastViewedProductId.value = savedLastViewedId
    }
  }

  // 初始化數據
  async function initialize() {
    initializeFilters()
    if (selectedCategoryId.value) {
      // 有已儲存的分類篩選：需先載入分類樹才能展開子分類，維持先後順序
      await fetchCategories()
      await fetchProducts()
    } else {
      // 無分類篩選：分類與商品並行載入，縮短等待
      await Promise.all([fetchCategories(), fetchProducts()])
    }
    scrollToLastViewedProduct()
  }

  return {
    // 數據
    products,
    loading,
    selectedProductIds,
    searchText,
    skuSearchText,
    selectedCategoryId,
    selectedStatus,
    sortField,
    sortOrder,
    categories,
    categoryTreeData,
    paginationConfig,
    currentPage,
    pageSize,
    totalItems,
    totalPages,

    // 方法
    fetchProducts,
    fetchCategories,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    handleStatusChange,
    handleListedChange,
    handleDeleteSelected,
    handleCopyProduct,
    onSearchInput,
    clearSearchText,
    onSkuSearchInput,
    clearSkuSearchText,
    onCategoryChange,
    onStatusChange,
    handleCategoryVisibleChange,
    resetFilters,
    navigateToCreateProduct,
    navigateToEdit,
    scrollToLastViewedProduct,
    goToFrontendProduct,
    copyProductName,
    copySku,
    getImageUrl,
    formatCurrency,
    formatDateTime,
    getCategoryFullPath,
    getCategoryShortPath,
    initialize
  }
}

